import "../Login/login.scss";
import type * as React from "react";
import {
    type FC,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { updateUser } from "../../Actions/UserActions";
import AuthShell from "../../Common/AuthShell/AuthShell";
import BoxForm from "../../Common/BoxForm/BoxForm";
import Button from "../../Common/Button";
import Input from "../../Common/Input/Input";
import PasswordInput from "../../Common/Input/PasswordInput";
import { type RootState } from "../../Reducers/DeveloperReducers";
import { useTitle } from "../../Utilities/TitleHook";

const Register: FC = () => {
    useTitle("Register");

    const dispatch = useDispatch();

    const user = useSelector<RootState>(state => state.user.user);

    const [ name, setName ] = useState("");
    const [ username, setUsername ] = useState("");
    const [ password, setPassword ] = useState("");
    const [ email, setEmail ] = useState("");
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState<null | ReactNode>(null);
    const justRegistered = useRef(false);

    useEffect(() => {
        if(!user) {
            return;
        }

        // Already-logged-in visitors (e.g. via "Get Started") go straight to their applications, same
        // as Login.tsx - only a user who just submitted this form gets the welcome/create-dialog flag.
        const destination = justRegistered.current ? "/applications?welcome=1" : "/applications";

        window.location.assign(destination);
    }, [ user ]);

    if(user) {
        return null;
    }

    let registerText: React.ReactNode = "Register";

    if(loading) {
        registerText = <FontAwesomeLight icon="spinner-third" spin />;
    }

    const onSubmit = (): void => {
        // Check that we aren't already loading
        if(loading) {
            return;
        }

        // Check that we have valid details
        if(!username) {
            setError("Invalid Username");

            return;
        }

        if(!email) {
            setError("Invalid Email");

            return;
        }

        if(!password) {
            setError("Invalid Password");

            return;
        }

        if(!name) {
            setError("Invalid Name");

            return;
        }

        setLoading(true);

        fetch("register", {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                name,
                email,
                username,
                password,
            }),
        })
            .then((response) => {
                setLoading(false);

                if(response.status === 422) {
                    response.json()
                        .then((result) => {
                            const errors = [];

                            for(const i in result.errors) {
                                if(!Object.prototype.hasOwnProperty.call(result.errors, i)) continue;

                                for(let j = 0, l = result.errors[i].length; j < l; j++) {
                                    errors.push(result.errors[i][j]);
                                }
                            }

                            if(errors.length === 1) {
                                setError(errors[0]);
                            } else {
                                setError(
                                    <div className="whitespace-nowrap">
                                        {errors.map((err, index) => (
                                            <div key={index}>
                                                {err}
                                            </div>
                                        ))}
                                    </div>
                                );
                            }
                        });

                    return false;
                }

                if(response.status !== 200) {
                    throw new Error("Invalid details provided, please ensure all fields are valid.");
                }

                return response.json();
            })
            .then((result) => {
                if(result === false) {
                    return;
                }

                justRegistered.current = true;
                dispatch(updateUser(result));
            })
            .catch((err) => {
                console.error(err);

                if(err instanceof Error) {
                    setError(err.message);
                } else {
                    setError(err.toString());
                }
            });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        // Check to see if ENTER was pressed
        if(e.key === "Enter") {
            onSubmit();
        }
    };

    return (
        <AuthShell>
            <BoxForm
                error={error}
                onClear={() => {
                    setError(null);
                }}
            >
                <h1>Register</h1>
                <Input
                    icon="id-card"
                    label="Name"
                    type="text"
                    showLabel={false}
                    onChange={(e) => {
                        setName(e.currentTarget.value);
                        setError(null);
                    }}
                    value={name}
                    onKeyDown={onKeyDown}
                />
                <Input
                    icon="envelope"
                    label="Email"
                    type="email"
                    showLabel={false}
                    onChange={(e) => {
                        setEmail(e.currentTarget.value);
                        setError(null);
                    }}
                    value={email}
                    onKeyDown={onKeyDown}
                />
                <Input
                    icon="user"
                    label="Username"
                    type="text"
                    showLabel={false}
                    onChange={(e) => {
                        setUsername(e.currentTarget.value);
                        setError(null);
                    }}
                    value={username}
                    onKeyDown={onKeyDown}
                />
                <PasswordInput
                    icon="key"
                    label="Password"
                    onChange={(e) => {
                        setPassword(e.currentTarget.value);
                        setError(null);
                    }}
                    value={password}
                    onKeyDown={onKeyDown}
                />

                <Button onClick={onSubmit}>{registerText}</Button>

                <p className="auth-form-footer">
                    <Link to="/login">
                        Already have an account? <span className="link-primary">Log in!</span>
                    </Link>
                </p>
            </BoxForm>
        </AuthShell>
    );
};

export default Register;
